import React, { useRef, useCallback, useState } from 'react'
import styled, { css } from 'styled-components'
import Loader from 'react-loader-spinner'
import { IconTrash } from '@loadsmart/icons'

import ExcelLogo from 'assets/icons/excel-logo.svg'
import { truncateFileName } from 'utils/strings'

const hasFileCSS = css`
  border: 1px solid ${({ theme }) => theme.colors.backgroundSecondary};
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
`
const defaultCSS = css`
  border: 8px dashed ${({ theme }) => theme.colors.backgroundLight};
`
const hasErrorCSS = css`
  border: 4px dashed ${({ theme }) => theme.colors.danger};
`
const Input = styled.input`
  display: none;
`
const Div = styled.div<{
  hasFile?: boolean
  hasError?: boolean
  disabled?: boolean
}>`
  border-radius: 4px;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.black};
  cursor: pointer;
  font-size: 16px;
  height: 20rem;
  line-height: 21px;
  text-align: center;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  ${({ disabled }) => disabled && `pointer-events: none;`}

  ${({ hasFile, hasError }) => {
    if (hasError) return hasErrorCSS
    if (hasFile) return hasFileCSS
    return defaultCSS
  }}
`
const Content = styled.div`
  display: flex;
  align-items: center;
  flex-grow: 1;
  position: relative;
  top: 18px;
`
const RemoveButton = styled.button`
  display: flex;
  align-items: center;
  position: relative;
  bottom: 35px;
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.colors.borderSecondary};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-family: Sharp Sans;
  font-size: 12px;
  font-weight: bold;
  line-height: 12px;
  padding: 10px;
  text-align: center;
  text-transform: uppercase;
  cursor: pointer;
  background: transparent;
`

interface Props
  extends Omit<React.HTMLAttributes<HTMLInputElement>, 'onChange'> {
  value: File | undefined | null
  name?: string
  accept?: string[]
  error?: boolean
  isLoading?: boolean
  onChange(file: File | undefined): void
}

export default function FileInput({
  onChange,
  value,
  accept,
  error,
  isLoading,
  className,
  ...props
}: Props) {
  const [isDragging, setIsDragging] = useState(false)
  const fileInput = useRef<HTMLInputElement>(null)
  const onClick = useCallback(() => {
    fileInput?.current?.click()
  }, [fileInput])
  const onChangeInput = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event?.currentTarget?.files?.[0]
      onChange(file)
    },
    [onChange]
  )
  const onDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault()
      if (isDragging) setIsDragging(false)
      const file = event?.dataTransfer?.files[0]
      onChange(file)
    },
    [isDragging, onChange]
  )
  const onDragStart = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
  }, [])
  const onDragOver = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault()
      if (!isDragging) setIsDragging(true)
    },
    [isDragging]
  )
  const onDragLeave = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault()
      if (isDragging) setIsDragging(false)
    },
    [isDragging]
  )
  const removeFile = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation()
      event.preventDefault()
      onChange(undefined)
    },
    [onChange]
  )
  const onKeyPress = useCallback((event: React.KeyboardEvent) => {
    if (event.key !== 'Enter') return
    fileInput?.current?.click()
  }, [])
  const getBody = useCallback(() => {
    if (isLoading) return <Loader type="TailSpin" />
    if (isDragging) return 'Release to upload the file'
    if (value) {
      return (
        <>
          <Content>
            <object type="image/svg+xml" data={ExcelLogo}>
              Excel Logo
            </object>
            &nbsp; &nbsp;
            <p>{truncateFileName(value.name)}</p>
          </Content>
          <RemoveButton type="button" onClick={removeFile}>
            <IconTrash height={18} width={18} />
            &nbsp; REMOVE FILE
          </RemoveButton>
        </>
      )
    }
    return 'Drag file here or click this area to browse'
  }, [isDragging, isLoading, removeFile, value])

  return (
    <>
      <Input
        {...props}
        onChange={onChangeInput}
        type="file"
        ref={fileInput}
        accept={accept ? accept.join(',') : undefined}
        disabled={isLoading}
      />
      <Div
        tabIndex={0}
        role="button"
        aria-pressed="false"
        onClick={onClick}
        onDrop={onDrop}
        onDragStart={onDragStart}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onKeyPress={onKeyPress}
        hasFile={!!value}
        hasError={error}
        disabled={isLoading}
        className={className}
      >
        {getBody()}
      </Div>
    </>
  )
}
